import { AnchorEntry, EntityType, Entry, NoteEntry, ReferenceEntry } from '../types';

/**
 * Recursively resolves all entry IDs reachable from the given seed IDs via the
 * anchor/reference↔reference/note link graph:
 *
 *   anchor → refs/notes  (ref.anchorId === anchor.id, note.anchorId === anchor.id)
 *   ref    → anchors     (anchor.referenceId === ref.id) → their refs/notes → …
 *   ref    → notes       (note.anchorId === ref.id) — a note pinned directly to a reference card
 *
 * Seeds are always included in the returned array. Traversal is breadth-first
 * and terminates when no new IDs are discovered, making it safe for arbitrary
 * nesting depth.
 */
export function resolveLinkedGroupIds(seedIds: string[], entries: Entry[]): string[] {
	const anchors = entries.filter((e): e is AnchorEntry => e.type === EntityType.Anchor);
	const refs = entries.filter((e): e is ReferenceEntry => e.type === EntityType.Reference);
	const notes = entries.filter((e): e is NoteEntry => e.type === EntityType.Note);

	const collected = new Set<string>(seedIds);
	const frontier = [...seedIds];

	while (frontier.length > 0) {
		const id = frontier.pop();
		const entry = entries.find((e) => e.id === id);
		if (!entry) continue;

		if (entry.type === EntityType.Anchor) {
			for (const ref of refs) {
				if (ref.anchorId === id && !collected.has(ref.id)) {
					collected.add(ref.id);
					frontier.push(ref.id);
				}
			}
			for (const note of notes) {
				if (note.anchorId === id && !collected.has(note.id)) {
					collected.add(note.id);
					frontier.push(note.id);
				}
			}
		} else if (entry.type === EntityType.Reference) {
			for (const anchor of anchors) {
				if (anchor.referenceId === id && !collected.has(anchor.id)) {
					collected.add(anchor.id);
					frontier.push(anchor.id);
				}
			}
			for (const note of notes) {
				if (note.anchorId === id && !collected.has(note.id)) {
					collected.add(note.id);
					frontier.push(note.id);
				}
			}
		}
	}

	return Array.from(collected);
}
